import React, { useState } from 'react';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { PASSWORD_RULES } from '../passwordRules';
import Hint from './Hint';

/**
 * Two-step password reset: Cognito emails a code, then the code plus a new
 * password completes it.
 *
 * Only works for approved accounts. Approving sets email_verified, and Cognito
 * refuses to send a reset code to an address it hasn't verified — so a pending
 * registrant gets told to wait for approval rather than a cryptic error.
 *
 * The app client has "prevent user existence errors" on, so an unknown address
 * advances to the code step rather than erroring. The copy is hedged to match:
 * claiming an email was sent would be untrue for an address with no account.
 */
export default function ForgotPassword({
  initialEmail = '',
  onBackToLogin,
}: {
  initialEmail?: string;
  onBackToLogin: () => void;
}): JSX.Element {
  const [step, setStep] = useState<'request' | 'confirm' | 'done'>('request');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Enter the email you registered with.');
      return;
    }

    setBusy(true);
    try {
      await resetPassword({ username: email.trim() });
      setStep('confirm');
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!code.trim() || !password) {
      setError('Enter the code from your email and a new password.');
      return;
    }

    setBusy(true);
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: code.trim(),
        newPassword: password,
      });
      setStep('done');
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'done') {
    return (
      <div className="card">
        <h2>✅ Password Changed</h2>
        <p>You can sign in with your new password now.</p>
        <div className="form-actions">
          <button type="button" onClick={onBackToLogin}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="card auth-card">
        <h2>📬 Check Your Email</h2>
        <p>
          If <strong>{email}</strong> is a registered, approved account, a code is on its
          way. Enter it below with your new password — codes expire in 24 hours.
        </p>
        <p className="attendee-meta">
          Don't see it? Check your junk or spam folder — mail from
          noreply@southeastcogcamping.com sometimes lands there.
        </p>

        <form onSubmit={handleConfirm}>
          <label>
            Code from email
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={e => setCode(e.target.value)}
            />
          </label>

          <label>
            <span className="label-row">
              New password
              <Hint label={`Password must contain: ${PASSWORD_RULES.join(', ')}`}>
                <strong>Password must contain:</strong>
                <ul>
                  {PASSWORD_RULES.map(rule => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </Hint>
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </label>

          {error && <p className="error">{error}</p>}

          <div className="form-actions">
            <button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Set New Password'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => {
                setStep('request');
                setError('');
                setCode('');
              }}
            >
              Back
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="card auth-card">
      <h2>🔑 Forgot Password</h2>
      <p>
        Enter the email you registered with and we'll send you a code to set a new
        password.
      </p>

      <form onSubmit={handleRequest}>
        <label>
          Email
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </label>

        {error && <p className="error">{error}</p>}

        <div className="form-actions">
          <button type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send Code'}
          </button>
          <button type="button" className="btn-secondary" disabled={busy} onClick={onBackToLogin}>
            Back to Login
          </button>
        </div>
      </form>
    </div>
  );
}

function messageFor(err: unknown): string {
  const name = (err as { name?: string })?.name;
  const message = (err as { message?: string })?.message || '';

  switch (name) {
    case 'UserNotFoundException':
      return 'No account found for that email.';
    case 'InvalidParameterException':
      // Raised when the account has no verified email — i.e. not approved yet.
      return message.includes('verified')
        ? "Your account hasn't been approved yet, so there's no verified email to send a code to. Ask an organizer to approve you first."
        : message || 'Something went wrong. Please try again.';
    case 'CodeMismatchException':
      return 'That code is not right. Check the email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Go back and request a new one.';
    case 'InvalidPasswordException':
      return 'That password does not meet the minimum requirements.';
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'UserNotConfirmedException':
      return "Your account hasn't been approved yet. Ask an organizer to approve you first.";
    default:
      return message || 'Something went wrong. Please try again.';
  }
}
